import { Check } from 'lucide-react'
import { ComponentRenderer } from './InteractiveComponents'
import { MESSAGE_STATUS } from '../../contexts/SSEEvents'
import type { MessageStatus } from '../api'

interface QuestionFormToolData {
  toolName: string
  toolInput: {
    context?: string
    questions?: Array<{
      question: string
      type: string
      required?: boolean
      options?: string[]
    }>
  }
  startedAt: string
  completedAt?: string
  status: 'running' | 'completed'
  componentType?: string
  componentData?: {
    questions?: Array<{
      id: string
      text: string
      type: string
      required?: boolean
      options?: unknown
    }>
  }
  responses?: Record<string, string | string[] | { question: string; answer: string }>
}

interface BotMessageToolFormProps {
  toolData: QuestionFormToolData
  status?: MessageStatus
  msgId: string
  onSubmit: (msgId: string, data: Record<string, unknown>) => void
}

export default function BotMessageToolForm({
  toolData,
  status,
  msgId,
  onSubmit,
}: BotMessageToolFormProps) {
  if (status === MESSAGE_STATUS.FAILED) {
    return (
      <div className="pl-3 py-0.5 border-l-2 border-destructive/40">
        <div className="text-body text-foreground leading-relaxed">
          Failed to load questions. Please try again.
        </div>
      </div>
    )
  }

  if (status === MESSAGE_STATUS.IN_PROGRESS || toolData.status === 'running') {
    const questions = toolData.componentData?.questions || []
    if (questions.length === 0) return null

    const interactiveComponent = {
      type: toolData.componentType || 'questions',
      id: msgId,
      data: toolData.componentData || {},
      answered: false,
    }

    return (
      <div className="w-full">
        <ComponentRenderer component={interactiveComponent} onSubmit={onSubmit} />
      </div>
    )
  }

  if (status === MESSAGE_STATUS.PROCESSED || toolData.status === 'completed') {
    const responses = toolData.responses
    if (!responses || Object.keys(responses).length === 0) return null

    return (
      <div className="rounded-lg border border-border bg-card p-3 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Check size={14} strokeWidth={2} className="text-success" />
          <span className="text-label text-foreground">Questions Answered</span>
        </div>

        <div className="flex flex-col gap-3">
          {Object.entries(responses).map(([questionId, response]) => {
            if (!response) return null

            if (
              typeof response === 'object' &&
              !Array.isArray(response) &&
              'question' in response &&
              'answer' in response
            ) {
              return (
                <div key={questionId} className="flex flex-col gap-1">
                  <div className="text-caption text-muted-foreground">{response.question}</div>
                  <div className="text-body text-foreground pl-3 border-l-2 border-primary">
                    {response.answer}
                  </div>
                </div>
              )
            }

            const questions = toolData.componentData?.questions || []
            const question = questions.find((q) => q.id === questionId)
            if (!question) return null

            const answerText = Array.isArray(response) ? response.join(', ') : String(response)

            return (
              <div key={questionId} className="flex flex-col gap-1">
                <div className="text-caption text-muted-foreground">{question.text}</div>
                <div className="text-body text-foreground pl-3 border-l-2 border-primary">
                  {answerText}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return null
}
